import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, OutlinedInput, RadioGroup, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { locale } from '../../config/language.config';
import { useGlobalStateContext } from '../../context/GlobalStateContext/GlobalStateContext';
import { useSettingsContext } from '../../context/SettingsContext/SettingsContext';
import { useIsDesktop, useIsTablet } from '../../utils/mediaQueries';
import { stripNumbersAndSpecialChars } from '../../utils/string.helpers';
import { ActionButton } from '../Button/Button';
import { CustomSwitch } from '../CustomSwitch/CustomSwitch';
import { DialogBox } from '../DialogBox/DialogBox';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { RadioButton } from '../RadioButton/RadioButton';
import SettingsSkeleton from './SettingsSkeleton';
import styles from './styles';
export const Settings = (props) => {
    const { cancelHandler } = props;
    const [preferredName, setPreferredName] = React.useState('');
    const navigate = useNavigate();
    const { globalState: { merchant, merchantUpdated, loading }, showSnackBar, updateUserContext, updateGlobalState, } = useGlobalStateContext();
    const { settingsContextState: { saveChangesOpen, backClicked }, updateSettingsContextState, } = useSettingsContext();
    const [error, setError] = React.useState('');
    const { t: text, i18n: { resolvedLanguage = 'en' } = {} } = useTranslation();
    const langLocale = locale[resolvedLanguage]; //en-CA or fr-CA
    const { notifOnboardingStatus, notifLanguage: apiNotifLanguage, displayLanguage: apiDisplayLanguage = langLocale, } = merchant;
    const isDesktop = useIsDesktop();
    const isTablet = useIsTablet();
    const isLargeDesktop = !isTablet && isDesktop;
    const [displayLanguage, setDisplayLanguage] = useState(apiDisplayLanguage);
    const [notifLanguage, setNotifLanguage] = useState(apiNotifLanguage || langLocale);
    const [inSubmit, setInSubmit] = useState(false);
    const [switchValue, setSwitchValue] = useState(notifOnboardingStatus);
    const languages = [locale.en, locale.fr];
    const [initialformState, setInitialformState] = useState({
        preferredName: '',
        notifLanguage: '',
        notifOnboardingStatus: notifOnboardingStatus,
        displayLanguage: '',
    });
    const [dialog, setDialog] = useState('unsaved');
    useEffect(() => {
        return () => {
            updateGlobalState({ merchantUpdated: null });
        };
    }, []);
    useEffect(() => {
        const { preferredName, notifOnboardingStatus, notifLanguage, displayLanguage } = merchant;
        setPreferredName(preferredName || '');
        setDisplayLanguage(displayLanguage || langLocale);
        setNotifLanguage(notifLanguage || langLocale);
        setSwitchValue(notifOnboardingStatus);
        setInitialformState({
            preferredName: preferredName || '',
            notifLanguage: notifLanguage || langLocale,
            notifOnboardingStatus,
            displayLanguage: displayLanguage || langLocale,
        });
    }, [merchant]);
    useEffect(() => {
        if (merchantUpdated) {
            updateSettingsContextState({ formChanged: false });
            showSnackBar(text('SETTING_PREFERENCES_SAVED'));
        }
        if (merchantUpdated === false) {
            showSnackBar(text('SETTING_PREFERENCES_NOT_SAVED'), 'error');
        }
    }, [merchantUpdated]);
    useEffect(() => {
        if (settingsWereChanged()) {
            updateSettingsContextState({ formChanged: true });
        }
        else {
            updateSettingsContextState({ formChanged: false });
        }
    }, [preferredName, displayLanguage, switchValue, notifLanguage]);
    const settingsWereChanged = () => {
        return (preferredName !== initialformState.preferredName ||
            displayLanguage !== initialformState.displayLanguage ||
            notifLanguage !== initialformState.notifLanguage ||
            switchValue !== initialformState.notifOnboardingStatus);
    };
    const revertChanges = () => {
        setPreferredName(initialformState.preferredName);
        setDisplayLanguage(initialformState.displayLanguage);
        setNotifLanguage(initialformState.notifLanguage);
        setSwitchValue(initialformState.notifOnboardingStatus);
    };
    const handleOnChange = (e) => {
        setError('');
        setInSubmit(false);
        const inputValue = e.target.value;
        let nameStr = stripNumbersAndSpecialChars(inputValue);
        if (nameStr.length > 25) {
            nameStr = nameStr.slice(0, 25);
        }
        setPreferredName(nameStr);
    };
    const handleSubmit = async () => {
        if (initialformState.notifOnboardingStatus !== switchValue) {
            if (switchValue) {
                setDialog('enable-sms');
            }
            else {
                setDialog('disable-sms');
            }
            updateSettingsContextState({ formChanged: true, saveChangesOpen: true });
            return;
        }
        setInSubmit(true);
        if (!preferredName || error) {
            setError(text('ONBOARDING_CHANGE_NAME_INPUT_ERROR'));
            return false;
        }
        try {
            updateGlobalState({ merchantUpdated: null });
            await updateUserContext({
                preferredName,
                notifLanguage,
                notifOnboardingStatus,
                displayLanguage,
            });
        }
        catch (e) {
            console.log('Update settings failed', e);
        }
    };
    const handleRadioChange = (e, callback) => {
        setInSubmit(false);
        if (typeof callback === 'function') {
            callback(e.target.value);
        }
    };
    useEffect(() => {
        if (backClicked) {
            if (settingsWereChanged()) {
                setDialog('unsaved');
            }
            /*  updateSettingsContextState({ formChanged: true, saveChangesOpen: true });*/
        }
    }, [backClicked]);
    /*---------------Dialog component------------------*/
    const SettingsDialogBox = ({ dialog }) => {
        const dialogProps = {
            unsaved: {
                title: text('SETTING_UNSAVED_TITLE'),
                text: text('SETTING_UNSAVED_TEXT'),
                handleClose: () => {
                    updateSettingsContextState({ saveChangesOpen: false });
                },
                secondaryClick: () => {
                    updateSettingsContextState({ saveChangesOpen: false, backClicked: false });
                    revertChanges();
                    if (backClicked) {
                        navigate(-1);
                    }
                },
                primaryClick: async () => {
                    // if (backClicked) {
                    updateSettingsContextState({ saveChangesOpen: false, backClicked: false });
                    /*   navigate(-1);
                    }*/
                    updateGlobalState({ merchantUpdated: null });
                    await updateUserContext({
                        preferredName,
                        notifLanguage,
                        notifOnboardingStatus,
                        displayLanguage,
                    });
                    if (switchValue !== initialformState.notifOnboardingStatus) {
                        if (switchValue) {
                            navigate('/sms-setup');
                        }
                        return;
                    }
                },
                primaryText: text('SETTING_SAVE_BUTTON'),
                secondaryText: text('SETTING_DISCARD_BUTTON'),
            },
            'enable-sms': {
                title: text('SETTING_ENABLE_SMS_TITLE'),
                text: text('SETTING_ENABLE_SMS_TEXT'),
                handleClose: () => {
                    updateSettingsContextState({ saveChangesOpen: false });
                },
                secondaryClick: () => {
                    updateSettingsContextState({ saveChangesOpen: false, backClicked: false });
                    revertChanges();
                },
                primaryClick: () => {
                    updateSettingsContextState({ saveChangesOpen: false, backClicked: false });
                    navigate('/sms-setup');
                },
                primaryText: text('CONTINUE'),
                secondaryText: text('SETTING_CANCEL_BUTTON'),
            },
            'disable-sms': {
                title: text('SETTING_DISABLE_SMS_TITLE'),
                text: text('SETTING_DISABLE_SMS_TEXT'),
                handleClose: () => {
                    updateSettingsContextState({ saveChangesOpen: false });
                },
                secondaryClick: () => {
                    updateSettingsContextState({ saveChangesOpen: false, backClicked: false });
                    revertChanges();
                },
                primaryClick: async () => {
                    updateSettingsContextState({ saveChangesOpen: false, backClicked: false });
                    updateGlobalState({ merchantUpdated: null });
                    await updateUserContext({
                        preferredName,
                        notifLanguage,
                        notifOnboardingStatus: 0,
                        displayLanguage,
                    });
                },
                primaryText: text('CONTINUE'),
                secondaryText: text('SETTING_CANCEL_BUTTON'),
            },
        };
        const props = dialogProps[dialog];
        const { title, text: dialogText, handleClose, secondaryClick, primaryClick, primaryText, secondaryText, } = props;
        return (_jsx(DialogBox, { title: title, text: dialogText, showDialog: saveChangesOpen, handleClose: handleClose, children: _jsxs(_Fragment, { children: [_jsx(ActionButton, { type: 'secondary', text: secondaryText, onClick: secondaryClick, sectionName: 'Setting Details' }), _jsx(ActionButton, { type: 'primary', text: primaryText, onClick: primaryClick, sectionName: 'Setting Details' })] }) }));
    };
    return loading ? (_jsx(Box, { padding: 4, children: _jsx(SettingsSkeleton, {}) })) : (_jsx(Box, { sx: styles.settingsWrapper, children: _jsxs(Box, { sx: styles.pageContentWrapper(isLargeDesktop), children: [_jsx(Box, { sx: styles.cardHeader2, children: _jsx(Typography, { variant: 'h2', children: text('SETTING_SUBTITLE2') }) }), _jsxs(Box, { mb: 4, children: [_jsx("label", { htmlFor: 'preferredName', children: _jsx(Typography, { mb: 1, variant: 'body2', children: text('SETTING_INPUT_LABEL') }) }), _jsx(OutlinedInput, { value: preferredName, "data-testid": 'preferredName', error: false, fullWidth: true, sx: { background: 'white' }, onChange: handleOnChange, name: 'preferredName', id: 'preferredName' }), _jsx(Box, { sx: { ml: '-0.5rem' }, children: error && _jsx(ErrorMessage, { type: 'Plain', message: error }) })] }), _jsxs(Box, { mb: 4, className: 'larger', children: [_jsx(Typography, { variant: 'body1', sx: { mb: { desktop: 0, mobile: 2 } }, children: text('SETTING_LANGUAGE_LABEL') }), _jsx(RadioGroup, { children: _jsxs(Box, { sx: {
                                    display: 'flex',
                                    gap: { desktop: '4rem', mobile: '0' },
                                    flexDirection: { desktop: 'row', mobile: 'column' },
                                }, children: [_jsx(RadioButton, { label: 'English', value: locale.en, isChecked: displayLanguage === locale.en, onChange: (e) => {
                                            handleRadioChange(e, setDisplayLanguage);
                                        }, sectionName: 'Setting Details' }, 'english'), _jsx(RadioButton, { label: 'Fran\u00E7ais', isChecked: displayLanguage === locale.fr, value: locale.fr, onChange: (e) => {
                                            handleRadioChange(e, setDisplayLanguage);
                                        }, sectionName: 'Setting Details' }, 'french')] }) }), !languages.includes(displayLanguage) && inSubmit && (_jsx(Box, { children: _jsx(ErrorMessage, { type: 'Plain', message: text('SET_UP_SMS_ERR_LANGUAGE') }) }))] }), _jsx(Box, { mb: 4, sx: { width: { desktop: '400px', mobile: '100%' } }, className: 'larger', children: _jsx("hr", {}) }), _jsx(Box, { sx: styles.cardHeader2, children: _jsx(Typography, { variant: 'h2', children: text('SETTING_SUBTITLE3') }) }), _jsx(Box, { mb: 4, children: _jsx(CustomSwitch, { labels: {
                            on: text('SETTING_SMS_LABEL_DISABLE'),
                            off: text('SETTING_SMS_LABEL_ENABLE'),
                        }, isChecked: !!switchValue, onChange: (e, checked) => {
                            setSwitchValue(checked ? 1 : 0);
                        }, sectionName: 'Setting Details' }) }), _jsxs(Box, { mb: 4, className: 'larger', children: [_jsx(Typography, { variant: 'body1', sx: { mb: { desktop: 0, mobile: 2 } }, children: text('SETTING_LANGUAGE_SMS') }), _jsx(RadioGroup, { children: _jsxs(Box, { sx: {
                                    display: 'flex',
                                    gap: { desktop: '4rem', mobile: '0' },
                                    flexDirection: { desktop: 'row', mobile: 'column' },
                                }, children: [_jsx(RadioButton, { label: 'English', value: locale.en, isChecked: notifLanguage === locale.en, onChange: (e) => {
                                            handleRadioChange(e, setNotifLanguage);
                                        }, sectionName: 'Setting Details' }, 'english-sms'), _jsx(RadioButton, { label: 'Fran\u00E7ais', isChecked: notifLanguage === locale.fr, value: locale.fr, onChange: (e) => {
                                            handleRadioChange(e, setNotifLanguage);
                                        }, sectionName: 'Setting Details' }, 'french-sms')] }) }), !languages.includes(notifLanguage) && inSubmit && (_jsx(Box, { children: _jsx(ErrorMessage, { type: 'Plain', message: text('SET_UP_SMS_ERR_LANGUAGE') }) }))] }), _jsxs(Box, { sx: styles.ctaButtonsWrapper, children: [_jsx(ActionButton, { type: 'secondary', text: text('SETTING_CANCEL_BUTTON'), onClick: () => {
                                cancelHandler();
                            }, sectionName: 'Setting Details' }), _jsx(ActionButton, { type: 'primary', text: text('SETTING_SAVE_BUTTON'), onClick: () => {
                                handleSubmit();
                            }, sectionName: 'Setting Details' })] }), _jsx(SettingsDialogBox, { dialog: dialog })] }) }));
};
